import { faArrowUpArrowDown, faCheck } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@shared/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@shared/components/ui/dropdown-menu';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

export type CommonSortType = 'asc' | 'desc' | 'recent' | 'units_asc';

export interface CommonSortDropdownProps {
  onSortChange: (sortType: CommonSortType) => void;
  defaultLabel?: string;
  dataTestId?: string;
  className?: string;
  namespace?: string;
  selectedSortType?: CommonSortType | null;
}

export const CommonSortDropdown = ({
  onSortChange,
  defaultLabel,
  dataTestId = 'common-sort-dropdown',
  className = '',
  namespace = 'pickingModule',
  selectedSortType = null,
}: CommonSortDropdownProps) => {
  const { t } = useTranslation(namespace);
  const [currentSortType, setCurrentSortType] = useState<CommonSortType | null>(selectedSortType);
  const [isOpen, setIsOpen] = useState(false);

  // Sincronizar con el prop selectedSortType
  useEffect(() => {
    setCurrentSortType(selectedSortType);
  }, [selectedSortType]);

  // Función para obtener el ícono de check si la opción está seleccionada
  const getCheckIcon = (sortType: CommonSortType) => {
    if (currentSortType === sortType) {
      return <FontAwesomeIcon icon={faCheck} className="w-3.5 h-3.5 text-white mr-2" />;
    }
    return <div className="w-3.5 h-3.5 mr-2" />;
  };

  // Función para manejar la selección de opción
  const handleSelect = (sortType: CommonSortType) => {
    setCurrentSortType(sortType);
    setIsOpen(false);
    onSortChange(sortType);
  };

  // Función para obtener el label actual
  const getCurrentLabel = () => {
    switch (currentSortType) {
      case 'asc':
        return t('products.sort_asc');
      case 'desc':
        return t('products.sort_desc');
      case 'recent':
        return t('products.sort_recent');
      case 'units_asc':
        return t('products.sort_units_asc');
      default:
        return defaultLabel || t('products.sort_button');
    }
  };

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={`border-card-border ${className}`}
          data-testid={`${dataTestId}-trigger`}
        >
          <FontAwesomeIcon icon={faArrowUpArrowDown} className="w-4 h-4 mr-2" />
          <span className="hidden sm:inline">{getCurrentLabel()}</span>
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="min-w-[140px]" data-testid={`${dataTestId}-content`}>
        <DropdownMenuItem onClick={() => handleSelect('asc')} data-testid={`${dataTestId}-option-asc`}>
          <div className="flex items-center">
            {getCheckIcon('asc')}
            {t('products.sort_asc')}
          </div>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleSelect('desc')} data-testid={`${dataTestId}-option-desc`}>
          <div className="flex items-center">
            {getCheckIcon('desc')}
            {t('products.sort_desc')}
          </div>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleSelect('recent')} data-testid={`${dataTestId}-option-recent`}>
          <div className="flex items-center">
            {getCheckIcon('recent')}
            {t('products.sort_recent')}
          </div>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleSelect('units_asc')} data-testid={`${dataTestId}-option-units-asc`}>
          <div className="flex items-center">
            {getCheckIcon('units_asc')}
            {t('products.sort_units_asc')}
          </div>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
